import { useState } from "react";
import { motion } from "framer-motion";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/ScrollReveal";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { FileText, Users, CheckSquare, Briefcase, Mail, Award, BookOpen, Users as UsersIcon, Network, Zap } from "lucide-react";

const scriptURL =
  "https://script.google.com/macros/s/AKfycbwICVMNteRU3nxEOJXMpe2onYLrLaSMvCLqzWh6BDWLNEtL5wbTt6fudQk_s3M7nTZP/exec";

export default function CareerPath() {
  const [submitState, setSubmitState] = useState("idle");
  const [statusMessage, setStatusMessage] = useState("");
  const [preferredDomain, setPreferredDomain] = useState("");

  const isSubmitting = submitState === "submitting";

  const handleSubmit = async (event) => {
    event.preventDefault();

    const form = event.currentTarget;
    setStatusMessage("");

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    setSubmitState("submitting");

    try {
      const formData = new FormData(form);
      
      formData.append("sourcePage", window.location.href);
      formData.append("userAgent", navigator.userAgent);

      await fetch(scriptURL, {
        method: "POST",
        body: formData,
        mode: "no-cors",
      });

      setSubmitState("success");
      setStatusMessage("✅ Application submitted successfully!");
      form.reset();
      setPreferredDomain("");
    } catch (error) {
      console.error(error);
      setSubmitState("error");
      setStatusMessage("❌ Submission failed. Please try again.");
    }
  };

  return (
    <div>
      <section className="relative py-24 md:py-32 overflow-hidden mesh-bg">
        <div className="absolute inset-0 grain-overlay" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#097DC3]/8 text-[#097DC3] text-sm font-semibold uppercase tracking-wider mb-6">
              Career Path
            </span>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-[#0A0A0A] mb-6">
              Start Your <span className="text-[#097DC3]">Career Journey</span> With Us
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-[#475569] max-w-2xl">
              We are excited to learn more about your goals and match you with the right opportunities. Fill out the form below and our team will review your application and follow up with next steps.
            </p>
          </motion.div>
        </div>
        <div className="absolute top-20 right-10 w-72 h-72 rounded-full bg-[#8CDDF6]/15 blur-3xl" />
      </section>

      {/* Selection Process Section */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-[#DBEEFF]/40 via-white to-[#DBEEFF]/40 padding-top-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="font-['Outfit'] text-3xl md:text-4xl font-bold tracking-tight text-[#0A0A0A] mb-4">
                Our Selection Process
              </h2>
              <p className="text-lg text-[#475569] max-w-2xl mx-auto">
                A transparent 5-step process designed to help us understand your fit and support your growth
              </p>
            </div>
          </ScrollReveal>

          <div className="relative">
            {/* Desktop Connection Line */}
            <div className="hidden md:block absolute top-16 left-0 right-0 h-1 bg-gradient-to-r from-[#097DC3]/20 via-[#097DC3]/40 to-[#097DC3]/20" />

            <StaggerContainer staggerChildren={0.1}>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-8 relative z-10">
                {[
                  { step: 1, title: "Submit Application", desc: "Fill out the application form with your details.", icon: FileText },
                  { step: 2, title: "Resume Review", desc: "Our team will review your application.", icon: CheckSquare },
                  { step: 3, title: "Short Task / Assessment", desc: "Complete a short task related to your skills.", icon: BookOpen },
                  { step: 4, title: "Interview", desc: "Connect with our team for an interview.", icon: Users },
                  { step: 5, title: "Offer Confirmation", desc: "Selected candidates will receive an offer.", icon: Mail }
                ].map((item, idx) => {
                  const IconComponent = item.icon;
                  return (
                    <StaggerItem key={idx}>
                      <motion.div
                        whileHover={{ y: -8, boxShadow: "0 12px 24px rgba(9,125,195,0.15)" }}
                        transition={{ duration: 0.3 }}
                        className="text-center"
                      >
                        <div className="flex flex-col items-center">
                          {/* Step Number Badge */}
                          <motion.div
                            initial={{ scale: 0 }}
                            whileInView={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 100, delay: idx * 0.1 }}
                            viewport={{ once: true }}
                            className="relative mb-6"
                          >
                            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#097DC3] to-[#0767A8] flex items-center justify-center shadow-lg">
                              <IconComponent size={28} className="text-white" />
                            </div>
                            <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[#097DC3] border-4 border-white flex items-center justify-center text-xs font-bold text-white">
                              {item.step}
                            </div>
                          </motion.div>

                          {/* Step Title and Description */}
                          <h3 className="font-['Outfit'] text-lg font-semibold text-[#0A0A0A] mb-2">
                            {item.title}
                          </h3>
                          <p className="text-sm text-[#475569] leading-relaxed">
                            {item.desc}
                          </p>
                        </div>
                      </motion.div>
                    </StaggerItem>
                  );
                })}
              </div>
            </StaggerContainer>
          </div>
        </div>
      </section>

      {/* What You'll Gain Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="font-['Outfit'] text-3xl md:text-4xl font-bold tracking-tight text-[#0A0A0A] mb-4">
                What You'll Gain
              </h2>
              <p className="text-lg text-[#475569] max-w-2xl mx-auto">
                Unlock valuable benefits and opportunities through our program
              </p>
            </div>
          </ScrollReveal>

          <StaggerContainer staggerChildren={0.08}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              {[
                { title: "Internship Completion Certificate", icon: Award, color: "from-[#097DC3]" },
                { title: "Letter of Recommendation", icon: FileText, color: "from-[#3DC0E7]" },
                { title: "Real-world Project Experience", icon: Briefcase, color: "from-[#097DC3]" },
                { title: "Full Mentorship Support", icon: UsersIcon, color: "from-[#3DC0E7]" },
                { title: "Networking Opportunities", icon: Network, color: "from-[#097DC3]" }
              ].map((item, idx) => (
                <StaggerItem key={idx}>
                  <motion.div
                    whileHover={{ y: -6, scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                    className={`p-6 rounded-2xl border border-slate-100 bg-white/60 backdrop-blur-sm shadow-sm hover:shadow-md transition-all duration-300 text-center group`}
                  >
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      whileInView={{ scale: 1, rotate: 0 }}
                      transition={{ type: "spring", stiffness: 100, delay: idx * 0.08 }}
                      viewport={{ once: true }}
                      className={`w-14 h-14 rounded-xl bg-gradient-to-br ${item.color} to-[#0767A8] flex items-center justify-center mx-auto mb-4 group-hover:shadow-lg group-hover:scale-110 transition-all duration-300`}
                    >
                      <item.icon size={24} className="text-white" />
                    </motion.div>
                    <h3 className="font-['Outfit'] text-base font-semibold text-[#0A0A0A] mb-1">
                      {item.title}
                    </h3>
                    {item.desc && (
                      <p className="text-xs text-[#475569]">{item.desc}</p>
                    )}
                  </motion.div>
                </StaggerItem>
              ))}
            </div>
          </StaggerContainer>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-[#DBEEFF]/40 via-white to-[#DBEEFF]/40 border-t border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="font-['Outfit'] text-3xl md:text-4xl font-bold tracking-tight text-[#0A0A0A] mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-[#475569]">
                Find answers to common questions about our career program
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  question: "Is this internship paid?",
                  answer: "Yes, our internship programs are paid. Compensation varies based on the role, experience level, and location. We provide competitive stipends that align with industry standards."
                },
                {
                  question: "Can freshers apply?",
                  answer: "Absolutely! We welcome freshers to apply. We have specific programs designed for students with no prior experience. Our mentorship and training will help you develop professional skills."
                },
                {
                  question: "Can students from any department apply?",
                  answer: "Yes, we have opportunities across various domains. Whether you're from engineering, commerce, arts, or any other background, we have roles suited to different skill sets and interests."
                },
                {
                  question: "Is work remote?",
                  answer: "We offer flexible work arrangements. Depending on the role and your preference, you can work remotely, from our office, or in a hybrid model. We'll discuss this during the interview process."
                },
                {
                  question: "What is the selection process?",
                  answer: "Our selection process has 5 stages: Application submission, Resume review, Technical assessment/task, Interview, and Offer confirmation. The entire process typically takes 2-3 weeks."
                },
                {
                  question: "What is the duration of the internship?",
                  answer: "Internship durations are flexible and typically range from 2-6 months depending on the program and your availability. We can also discuss project-based engagements for shorter durations."
                }
              ].map((faq, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: idx * 0.05 }}
                  viewport={{ once: true }}
                >
                  <Accordion type="single" collapsible className="">
                    <AccordionItem value={`faq-${idx}`} className="border-0">
                      <AccordionTrigger className="hover:no-underline py-4 px-4 md:px-6 rounded-lg bg-white border border-slate-200 hover:bg-[#097DC3]/5 transition-colors duration-300 group text-left text-base font-medium text-[#0A0A0A]">
                        <span className="flex items-center gap-3 flex-1">
                          <span className="text-[#097DC3] font-bold text-sm group-data-[state=open]:text-[#0A0A0A] transition-colors">
                            {String(idx + 1).padStart(2, '0')}
                          </span>
                          <span className="text-sm">{faq.question}</span>
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 md:px-6 py-4 text-[#475569] text-sm leading-relaxed mt-2 bg-[#F8FAFC] rounded-lg">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </motion.div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-gradient-to-b from-[#DBEEFF] via-white to-[#DBEEFF]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <div className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm">
              <form id="careerForm" onSubmit={handleSubmit} encType="multipart/form-data">
          <ScrollReveal>
            <div className="text-center mb-16">
              <h2 className="font-['Outfit'] text-3xl md:text-4xl font-bold tracking-tight text-[#0A0A0A] mb-4">
                Register Now!
              </h2>
            </div>
          </ScrollReveal>
                <div className="mb-8 pb-6 border-b border-slate-200">
                  <h3 className="text-sm font-bold text-[#097DC3] uppercase tracking-wider mb-4">1. Application Type</h3>
                  <select name="applicationType" className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-[#0A0A0A] focus:outline-none focus:border-[#097DC3] focus:ring-2 focus:ring-[#097DC3]/10 transition" required>
                    <option value="">Select application type</option>
                    <option value="Internship">Internship</option>
                    <option value="Full-Time Role">Full-Time Role</option>
                    <option value="Freelance / Contract">Freelance / Contract</option>
                  </select>
                </div>

                <div className="mb-8 pb-6 border-b border-slate-200">
                  <h3 className="text-sm font-bold text-[#097DC3] uppercase tracking-wider mb-4">2. Personal Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <input name="fullName" type="text" className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-[#0A0A0A] placeholder-slate-500 focus:outline-none focus:border-[#097DC3] focus:ring-2 focus:ring-[#097DC3]/10 transition" placeholder="Full Name" required />
                    <input name="email" type="email" className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-[#0A0A0A] placeholder-slate-500 focus:outline-none focus:border-[#097DC3] focus:ring-2 focus:ring-[#097DC3]/10 transition" placeholder="Email Address" required />
                    <input name="phone" type="tel" className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-[#0A0A0A] placeholder-slate-500 focus:outline-none focus:border-[#097DC3] focus:ring-2 focus:ring-[#097DC3]/10 transition" placeholder="Phone Number" required />
                  </div>
                </div>

                <div className="mb-8 pb-6 border-b border-slate-200">
                  <h3 className="text-sm font-bold text-[#097DC3] uppercase tracking-wider mb-4">3. Location Details</h3>
                  <input name="city" type="text" className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-[#0A0A0A] placeholder-slate-500 focus:outline-none focus:border-[#097DC3] focus:ring-2 focus:ring-[#097DC3]/10 transition" placeholder="City" required />
                </div>

                <div className="mb-8 pb-6 border-b border-slate-200">
                  <h3 className="text-sm font-bold text-[#097DC3] uppercase tracking-wider mb-4">4. Education Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <input
                      name="qualification"
                      type="text"
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-[#0A0A0A] placeholder-slate-500 focus:outline-none focus:border-[#097DC3] focus:ring-2 focus:ring-[#097DC3]/10 transition"
                      placeholder="Highest Qualification"
                      required
                    />
                    <input
                      name="college"
                      type="text"
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-[#0A0A0A] placeholder-slate-500 focus:outline-none focus:border-[#097DC3] focus:ring-2 focus:ring-[#097DC3]/10 transition"
                      placeholder="College / University Name"
                      required
                    />
                    <input
                      name="branch"
                      type="text"
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-[#0A0A0A] placeholder-slate-500 focus:outline-none focus:border-[#097DC3] focus:ring-2 focus:ring-[#097DC3]/10 transition"
                      placeholder="Branch / Degree"
                      required
                    />
                    <input
                      name="passingYear"
                      type="text"
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-[#0A0A0A] placeholder-slate-500 focus:outline-none focus:border-[#097DC3] focus:ring-2 focus:ring-[#097DC3]/10 transition"
                      placeholder="Current Year / Year of Passing"
                      required
                    />
                  </div>
                  <select name="studentStatus" className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-[#0A0A0A] focus:outline-none focus:border-[#097DC3] focus:ring-2 focus:ring-[#097DC3]/10 transition" required>
                    <option value="">Student / Graduate Status</option>
                    <option value="Student">Student</option>
                    <option value="Graduate">Graduate</option>
                  </select>
                </div>

                <div className="mb-8 pb-6 border-b border-slate-200">
                  <h3 className="text-sm font-bold text-[#097DC3] uppercase tracking-wider mb-4">5. Application Preferences</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      name="preferredDomain"
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-[#0A0A0A] placeholder-slate-500 focus:outline-none focus:border-[#097DC3] focus:ring-2 focus:ring-[#097DC3]/10 transition"
                      placeholder="Preferred Role"
                      value={preferredDomain}
                      onChange={(e) => setPreferredDomain(e.target.value)}
                      required
                    />
                    <select name="experienceLevel" className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-[#0A0A0A] focus:outline-none focus:border-[#097DC3] focus:ring-2 focus:ring-[#097DC3]/10 transition" required>
                      <option value="">Experience Level</option>
                      <option value="Fresher">Fresher</option>
                      <option value="1+ Year">1+ Year</option>
                      <option value="2+ Years">2+ Years</option>
                      <option value="Experienced">Experienced</option>
                    </select>
                  </div>
                </div>

                <div className="mb-8 pb-6 border-b border-slate-200">
                  <h3 className="text-sm font-bold text-[#097DC3] uppercase tracking-wider mb-4">6. Skills Section</h3>
                  <input
                    name="technicalSkills"
                    type="text"
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-[#0A0A0A] placeholder-slate-500 focus:outline-none focus:border-[#097DC3] focus:ring-2 focus:ring-[#097DC3]/10 transition"
                    placeholder="Technical Skills (comma separated)"
                    required
                  />
                </div>

                <div className="mb-8 pb-6 border-b border-slate-200">
                  <h3 className="text-sm font-bold text-[#097DC3] uppercase tracking-wider mb-4">7. Profile & Proof</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <input
                      name="resume"
                      type="url"
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-[#0A0A0A] placeholder-slate-500 focus:outline-none focus:border-[#097DC3] focus:ring-2 focus:ring-[#097DC3]/10 transition"
                      placeholder="Resume URL"
                      required
                    />
                    <input
                      name="linkedinUrl"
                      type="url"
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-[#0A0A0A] placeholder-slate-500 focus:outline-none focus:border-[#097DC3] focus:ring-2 focus:ring-[#097DC3]/10 transition"
                      placeholder="LinkedIn Profile URL"
                      required
                    />
                    <input
                      name="portfolioUrl"
                      type="url"
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-[#0A0A0A] placeholder-slate-500 focus:outline-none focus:border-[#097DC3] focus:ring-2 focus:ring-[#097DC3]/10 transition"
                      placeholder="GitHub / Portfolio URL (Optional)"
                    />
                  </div>
                </div>

                <div className="mb-8 pb-6 border-b border-slate-200">
                  <h3 className="text-sm font-bold text-[#097DC3] uppercase tracking-wider mb-4">8. Availability</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      name="startDate"
                      type="date"
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-[#0A0A0A] focus:outline-none focus:border-[#097DC3] focus:ring-2 focus:ring-[#097DC3]/10 transition"
                      required
                    />
                    <input
                      name="hoursPerDay"
                      type="number"
                      min="1"
                      max="12"
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-[#0A0A0A] placeholder-slate-500 focus:outline-none focus:border-[#097DC3] focus:ring-2 focus:ring-[#097DC3]/10 transition"
                      placeholder="Hours per Day Commitment"
                      required
                    />
                  </div>
                </div>

                <div className="mb-8 pb-6 border-b border-slate-200">
                  <h3 className="text-sm font-bold text-[#097DC3] uppercase tracking-wider mb-4">9. Screening Questions</h3>
                  <textarea
                    name="whySelect"
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-[#0A0A0A] placeholder-slate-500 focus:outline-none focus:border-[#097DC3] focus:ring-2 focus:ring-[#097DC3]/10 transition min-h-28"
                    placeholder="Why should we select you?"
                    required
                  />
                  <textarea
                    name="projectDescription"
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-[#0A0A0A] placeholder-slate-500 focus:outline-none focus:border-[#097DC3] focus:ring-2 focus:ring-[#097DC3]/10 transition min-h-28 mt-4"
                    placeholder="Describe one project or work you have done"
                    required
                  />
                </div>

                <div className="mb-8">
                  <h3 className="text-sm font-bold text-[#097DC3] uppercase tracking-wider mb-4">10. Declaration</h3>
                  <label className="flex items-start gap-3 p-4 bg-[#F8FAFC] border border-slate-200 rounded-lg cursor-pointer">
                    <input type="checkbox" name="declaration" className="mt-1 w-5 h-5 accent-[#097DC3] cursor-pointer" required />
                    <span className="text-sm text-[#475569] leading-relaxed">
                      I confirm all information provided is true and accurate, and I agree to the program terms and conditions.
                    </span>
                  </label>
                </div>

                <button className="w-full px-6 py-3.5 bg-[#097DC3] text-white rounded-lg font-semibold text-base hover:bg-[#0767A8] transition disabled:opacity-65 disabled:cursor-not-allowed" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Submitting..." : "Submit Application"}
                </button>

                {statusMessage && (
                  <p
                    className={`mt-4 text-center font-semibold ${
                      submitState === "success" ? "text-green-600" : "text-red-600"
                    }`}
                    role="status"
                  >
                    {statusMessage}
                  </p>
                )}
              </form>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
